/** @module @liquidefy/code-editor/workspace/workspace-collab @version 2.0.0 @license LQD Research LLC */

// ═══════════════════════════════════════════════════════════════════════════════
// §1 IMPORTS
// ═══════════════════════════════════════════════════════════════════════════════

import type {
  EditorResult,
  WorkspaceCollabState,
  CollabParticipant,
  CollabCursor,
  FollowModeState,
  Position,
  Selection,
  Timestamp,
} from '../code-editor-types.js';

import { ok, err, now } from '../code-editor.js';

// ═══════════════════════════════════════════════════════════════════════════════
// §2 STATE FACTORY — create initial collab state
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Create a fresh collab state for a new session.
 * The initiating user is added as the first participant with an active status.
 */
export function createCollabState(
  sessionId: string,
  userId: string
): WorkspaceCollabState {
  return {
    isActive: true,
    participants: [],
    cursors: [],
    followTarget: undefined,
  };
}

// ═══════════════════════════════════════════════════════════════════════════════
// §3 PARTICIPANT MANAGEMENT — add, remove, query participants
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Add a participant to the collab session.
 * Returns an error if the participant already exists.
 */
export function addParticipant(
  state: WorkspaceCollabState,
  participant: CollabParticipant
): EditorResult<WorkspaceCollabState> {
  const exists = state.participants.some((p) => p.id === participant.id);
  if (exists) {
    return err('PARTICIPANT_EXISTS', `Participant ${participant.id} is already in the session`);
  }
  const updated: WorkspaceCollabState = {
    ...state,
    participants: [...state.participants, participant],
  };
  return ok(updated, 'PARTICIPANT_ADDED', `Added participant ${participant.name}`);
}

/**
 * Remove a participant from the collab session.
 * Also removes their cursor from the cursor list.
 */
export function removeParticipant(
  state: WorkspaceCollabState,
  userId: string
): WorkspaceCollabState {
  const participants = state.participants.filter((p) => p.id !== userId);
  const cursors = state.cursors.filter((c) => c.participantId !== userId);
  // If someone was following the removed participant, disable follow mode
  const followTarget = state.followTarget === userId ? undefined : state.followTarget;
  return { ...state, participants, cursors, followTarget };
}

/**
 * Get all participants currently in the session.
 */
export function getParticipants(state: WorkspaceCollabState): CollabParticipant[] {
  return [...state.participants];
}

/**
 * Get a single participant by their user ID.
 */
export function getParticipant(
  state: WorkspaceCollabState,
  userId: string
): CollabParticipant | undefined {
  return state.participants.find((p) => p.id === userId);
}

/**
 * Get all participants who are actively editing (have an active file).
 * "Active" is defined as having an `activeFile` set on their record.
 */
export function getActiveParticipants(state: WorkspaceCollabState): CollabParticipant[] {
  return state.participants.filter((p) => typeof p.activeFile === 'string');
}

// ═══════════════════════════════════════════════════════════════════════════════
// §4 CURSOR TRACKING — remote cursor positions per participant
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Update the cursor position for a participant in a given file.
 * If a cursor entry for this participant already exists, it is replaced.
 */
export function updateCursorPosition(
  state: WorkspaceCollabState,
  userId: string,
  position: Position,
  fileUri: string,
  selection?: Selection
): WorkspaceCollabState {
  const cursor: CollabCursor = {
    participantId: userId,
    fileUri,
    position,
    selection,
    timestamp: now(),
  };
  const filtered = state.cursors.filter((c) => c.participantId !== userId);
  return { ...state, cursors: [...filtered, cursor] };
}

/**
 * Get all remote cursors (all participants' cursor states).
 */
export function getParticipantCursors(state: WorkspaceCollabState): CollabCursor[] {
  return [...state.cursors];
}

/**
 * Get the cursor for a specific participant.
 */
export function getCursorForParticipant(
  state: WorkspaceCollabState,
  userId: string
): CollabCursor | undefined {
  return state.cursors.find((c) => c.participantId === userId);
}

/**
 * Get all cursors visible in a specific file URI.
 */
export function getCursorsInFile(
  state: WorkspaceCollabState,
  fileUri: string
): CollabCursor[] {
  return state.cursors.filter((c) => c.fileUri === fileUri);
}

/**
 * Expire cursors older than the given age threshold in milliseconds.
 * Useful for removing stale cursor indicators when a participant goes idle.
 */
export function expireOldCursors(
  state: WorkspaceCollabState,
  maxAgeMs: number
): WorkspaceCollabState {
  const cutoff = now() - maxAgeMs;
  const cursors = state.cursors.filter((c) => c.timestamp >= cutoff);
  return { ...state, cursors };
}

// ═══════════════════════════════════════════════════════════════════════════════
// §5 FOLLOW MODE — viewport synchronization between participants
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Enable follow mode, tracking a target participant's viewport and file.
 * Returns an error if the target is not in the session, or if trying to follow oneself.
 */
export function enableFollowMode(
  state: WorkspaceCollabState,
  targetUserId: string
): EditorResult<WorkspaceCollabState> {
  const target = state.participants.find((p) => p.id === targetUserId);
  if (!target) {
    return err('PARTICIPANT_NOT_FOUND', `Cannot follow: participant ${targetUserId} not in session`);
  }
  const updated: WorkspaceCollabState = {
    ...state,
    followTarget: targetUserId,
  };
  return ok(updated, 'FOLLOW_MODE_ENABLED', `Now following ${target.name}`);
}

/**
 * Disable follow mode, restoring autonomous navigation.
 */
export function disableFollowMode(state: WorkspaceCollabState): WorkspaceCollabState {
  return { ...state, followTarget: undefined };
}

/**
 * Get the current follow mode state — target participant ID if active, undefined otherwise.
 */
export function getFollowModeState(state: WorkspaceCollabState): string | undefined {
  return state.followTarget;
}

/**
 * Check whether follow mode is currently active.
 */
export function isFollowing(state: WorkspaceCollabState): boolean {
  return state.followTarget !== undefined;
}

/**
 * Compute a FollowModeState describing who is following whom in the session.
 * The leader is whoever has followers — any participant whose ID appears as another's `isFollowing`.
 */
export function buildFollowModeState(
  state: WorkspaceCollabState,
  leaderId: string
): FollowModeState {
  const followers = state.participants
    .filter((p) => p.isFollowing === leaderId)
    .map((p) => p.id);
  return {
    leaderId,
    followers,
    isActive: followers.length > 0,
  };
}

// ═══════════════════════════════════════════════════════════════════════════════
// §6 PRESENCE — broadcast and query online/offline status
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Broadcast presence information for a participant.
 * Updates the participant record with the latest active file and following state.
 */
export function broadcastPresence(
  state: WorkspaceCollabState,
  userId: string,
  presence: { activeFile?: string; isFollowing?: string; color?: string }
): WorkspaceCollabState {
  const idx = state.participants.findIndex((p) => p.id === userId);
  if (idx === -1) return state;
  const participant = state.participants[idx]!;
  const updated: CollabParticipant = {
    ...participant,
    activeFile: presence.activeFile ?? participant.activeFile,
    isFollowing: presence.isFollowing ?? participant.isFollowing,
    color: presence.color ?? participant.color,
  };
  const participants = [
    ...state.participants.slice(0, idx),
    updated,
    ...state.participants.slice(idx + 1),
  ];
  return { ...state, participants };
}

/**
 * Get participants grouped by the file they currently have open.
 * Returns a map of fileUri → participant list.
 */
export function getParticipantsByFile(
  state: WorkspaceCollabState
): Map<string, CollabParticipant[]> {
  const map = new Map<string, CollabParticipant[]>();
  for (const p of state.participants) {
    if (p.activeFile) {
      const existing = map.get(p.activeFile) ?? [];
      existing.push(p);
      map.set(p.activeFile, existing);
    }
  }
  return map;
}

/**
 * Count how many participants are active (have an open file).
 */
export function activeParticipantCount(state: WorkspaceCollabState): number {
  return getActiveParticipants(state).length;
}

/**
 * Check whether a specific user is currently in the session.
 */
export function isParticipantInSession(state: WorkspaceCollabState, userId: string): boolean {
  return state.participants.some((p) => p.id === userId);
}

/**
 * End the collab session entirely, clearing all state.
 */
export function endCollabSession(state: WorkspaceCollabState): WorkspaceCollabState {
  return {
    isActive: false,
    participants: [],
    cursors: [],
    followTarget: undefined,
  };
}

/**
 * Get the collab session summary — participant count, active cursor count, follow mode info.
 */
export function getSessionSummary(state: WorkspaceCollabState): {
  participantCount: number;
  activeCursorCount: number;
  isFollowing: boolean;
  followTarget?: string;
} {
  return {
    participantCount: state.participants.length,
    activeCursorCount: state.cursors.length,
    isFollowing: state.followTarget !== undefined,
    followTarget: state.followTarget,
  };
}
